import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Filter, RefreshCw } from 'lucide-react';
import { DocumentSnapshot } from 'firebase/firestore';
import { getActivityLogs } from '../../services/activityLog.service';
import { ActivityLog, ActivityLogFilters, ActivityAction, ActivityEntityType } from '../../types/activityLog.types';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { formatSafeDate } from '../../utils/dateUtils';
import LoadingSpinner from '../../components/shared/LoadingSpinner';

const ACTION_LABELS: Record<ActivityAction, string> = {
  login: 'Logged in',
  logout: 'Logged out',
  lead_created: 'Created lead',
  lead_updated: 'Updated lead',
  lead_deleted: 'Deleted lead',
  lead_determination_changed: 'Changed determination',
  appointment_created: 'Created appointment',
  appointment_updated: 'Updated appointment',
  appointment_cancelled: 'Cancelled appointment',
  workload_created: 'Created workload',
  workload_assigned: 'Assigned workload',
  workload_updated: 'Updated workload',
  survey_submitted: 'Submitted survey',
  contract_signed: 'Signed contract',
  contract_created: 'Created contract',
  user_created: 'Created user',
  user_updated: 'Updated user',
  project_created: 'Created project',
  project_updated: 'Updated project',
  expense_submitted: 'Submitted expense',
  expense_approved: 'Approved expense',
  expense_rejected: 'Rejected expense',
  disposition_changed: 'Changed disposition',
  task_created: 'Created task',
  task_completed: 'Completed task',
};

const ENTITY_COLORS: Record<ActivityEntityType, string> = {
  lead: 'bg-blue-100 text-blue-700',
  appointment: 'bg-purple-100 text-purple-700',
  workload: 'bg-green-100 text-green-700',
  survey_response: 'bg-teal-100 text-teal-700',
  contract: 'bg-amber-100 text-amber-700',
  user: 'bg-pink-100 text-pink-700',
  project: 'bg-indigo-100 text-indigo-700',
  expense: 'bg-red-100 text-red-700',
  task: 'bg-orange-100 text-orange-700',
  session: 'bg-gray-100 text-gray-700',
};

const ROLE_BADGES: Record<string, string> = {
  provider_admin: 'bg-purple-100 text-purple-800',
  provider_user: 'bg-blue-100 text-blue-800',
  client_admin: 'bg-amber-100 text-amber-800',
  client_user: 'bg-green-100 text-green-800',
  field: 'bg-gray-100 text-gray-800',
};

const PAGE_SIZE = 50;

const ActivityLogPage: React.FC = () => {
  const { userProfile } = useAuth();
  const { projects } = useProject();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState<ActivityLogFilters>({});

  // Filter inputs
  const [filterAction, setFilterAction] = useState<string>('');
  const [filterEntityType, setFilterEntityType] = useState<string>('');
  const [filterProjectId, setFilterProjectId] = useState<string>('');

  const loadLogs = useCallback(async (loadMore = false) => {
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const result = await getActivityLogs(
        filters,
        PAGE_SIZE,
        loadMore ? lastDoc || undefined : undefined
      );

      if (loadMore) {
        setLogs(prev => [...prev, ...result.logs]);
      } else {
        setLogs(result.logs);
      }
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Error loading activity logs:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters, lastDoc]);

  useEffect(() => {
    loadLogs();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => {
    const newFilters: ActivityLogFilters = {};
    if (filterAction) newFilters.action = filterAction as ActivityAction;
    if (filterEntityType) newFilters.entityType = filterEntityType as ActivityEntityType;
    if (filterProjectId) newFilters.projectId = filterProjectId;
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilterAction('');
    setFilterEntityType('');
    setFilterProjectId('');
    setFilters({});
  };

  const formatTimestamp = (log: ActivityLog) => {
    try {
      const date = log.createdAt?.toDate?.();
      if (!date) return 'N/A';
      return date.toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  if (loading && logs.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <Activity className="h-7 w-7 text-indigo-600" />
          Activity Log
        </h1>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Activity className="h-7 w-7 text-indigo-600" />
          Activity Log
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
              showFilters ? 'border-indigo-500 text-indigo-600 bg-indigo-50' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </button>
          <button
            onClick={() => loadLogs()}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All actions</option>
                {Object.entries(ACTION_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
              <select
                value={filterEntityType}
                onChange={(e) => setFilterEntityType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All types</option>
                {Object.keys(ENTITY_COLORS).map((type) => (
                  <option key={type} value={type}>{type.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select
                value={filterProjectId}
                onChange={(e) => setFilterProjectId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"
            >
              Apply
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Activity Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No activity logs found.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {formatTimestamp(log)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{log.userName}</div>
                      <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-xs font-medium ${ROLE_BADGES[log.userRole] || 'bg-gray-100 text-gray-700'}`}>
                        {log.userRole?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {ACTION_LABELS[log.action] || log.action}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ENTITY_COLORS[log.entityType] || 'bg-gray-100 text-gray-700'}`}>
                        {log.entityType?.replace('_', ' ')}
                      </span>
                      {log.entityName && (
                        <div className="text-xs text-gray-500 mt-0.5">{log.entityName}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {log.projectName || '-'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-xs truncate">
                      {log.details ? JSON.stringify(log.details).slice(0, 80) : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="flex justify-center py-6">
          <button
            onClick={() => loadLogs(true)}
            disabled={loadingMore}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loadingMore ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Loading...
              </>
            ) : (
              <>Load More ({logs.length} loaded)</>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityLogPage;
